import React, { useState, useEffect } from 'react';
import {
  Zap,
  Plus,
  Trash2,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { ProjectAutomation, User } from '../../../shared/types.ts';
import { TRELLO_LABEL_PALETTE } from './KanbanBoard.tsx';
import { useEscapeKey } from '../../hooks/useEscapeKey.ts';

interface AutomationSettingsModalProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const AutomationSettingsModal: React.FC<AutomationSettingsModalProps> = ({
  projectId,
  isOpen,
  onClose,
}) => {
  useEscapeKey(isOpen, onClose);
  const { token } = useAuth();
  const [automations, setAutomations] = useState<ProjectAutomation[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Form states
  const [isCreating, setIsCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [triggerEvent, setTriggerEvent] = useState<
    'status_changed' | 'task_created' | 'checklist_completed'
  >('status_changed');
  const [fromStatus, setFromStatus] = useState('');
  const [toStatus, setToStatus] = useState('in_review');
  const [actionType, setActionType] = useState<
    'set_priority' | 'move_status' | 'assign_user' | 'add_label' | 'add_comment'
  >('set_priority');
  const [targetPriority, setTargetPriority] = useState('urgent');
  const [targetMoveStatus, setTargetMoveStatus] = useState('completed');
  const [targetUserId, setTargetUserId] = useState('');
  const [targetLabelColor, setTargetLabelColor] = useState('emerald');
  const [targetLabelName, setTargetLabelName] = useState('Verified');
  const [targetComment, setTargetComment] = useState('Automated workflow check completed.');

  const fetchAutomations = async () => {
    if (!token) return;
    try {
      setIsLoading(true);
      const res = await fetch(`/api/features/automations/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAutomations(data.automations || []);
      }
    } catch (err) {
      console.error('Failed to load automations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchAutomations();
      setIsCreating(false);
      if (token) {
        fetch('/api/users', { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => r.json())
          .then((d) => {
            setUsers(d.users || []);
            if (d.users?.length > 0 && !targetUserId) {
              setTargetUserId(d.users[0].id);
            }
          })
          .catch(() => {});
      }
    }
  }, [isOpen, projectId, token]);

  if (!isOpen) return null;

  const handleCreateAutomation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    const triggerCondition: Record<string, any> = {};
    if (triggerEvent === 'status_changed') {
      if (fromStatus) triggerCondition.fromStatus = fromStatus;
      triggerCondition.toStatus = toStatus;
    }

    const actionPayload: Record<string, any> = {};
    if (actionType === 'set_priority') {
      actionPayload.priority = targetPriority;
    } else if (actionType === 'move_status') {
      actionPayload.status = targetMoveStatus;
    } else if (actionType === 'assign_user') {
      actionPayload.userId = targetUserId;
    } else if (actionType === 'add_label') {
      actionPayload.labelColor = targetLabelColor;
      actionPayload.labelName = targetLabelName;
    } else if (actionType === 'add_comment') {
      actionPayload.comment = targetComment;
    }

    const defaultTitle =
      triggerEvent === 'checklist_completed'
        ? `When all checklist items complete → ${actionType}`
        : triggerEvent === 'task_created'
        ? `When a card is created → ${actionType}`
        : `When status changes to ${toStatus} → ${actionType}`;

    try {
      const res = await fetch('/api/features/automations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          projectId,
          title: title.trim() || defaultTitle,
          triggerEvent,
          triggerCondition,
          actionType,
          actionPayload,
        }),
      });

      if (res.ok) {
        await fetchAutomations();
        setIsCreating(false);
        setTitle('');
      }
    } catch (err) {
      console.error('Failed to save automation:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!token) return;
    try {
      await fetch(`/api/features/automations/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setAutomations((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error('Failed to delete automation:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-surface-900 rounded-3xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl border border-surface-200 dark:border-surface-800 overflow-hidden">
        {/* Header */}
        <div className="p-5 border-b border-surface-200 dark:border-surface-800 flex items-center justify-between bg-surface-50/50 dark:bg-surface-850">
          <div className="flex items-center space-x-3">
            <span className="p-2 rounded-xl bg-amber-500/10 text-amber-500">
              <Zap className="w-5 h-5" />
            </span>
            <div>
              <h3 className="font-bold text-sm text-surface-900 dark:text-surface-100">
                Board Workflow Automations (Butler Rules)
              </h3>
              <p className="text-[11px] text-surface-400">
                Real-time trigger-action rules that automatically move, label, assign, or comment on cards
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-xl text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {!isCreating && (
            <div className="flex justify-between items-center">
              <span className="text-xs font-bold uppercase tracking-wider text-surface-400">
                Active Board Rules ({automations.length})
              </span>
              <button
                onClick={() => setIsCreating(true)}
                className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors shadow-sm"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Create New Rule</span>
              </button>
            </div>
          )}

          {/* Creation Form */}
          {isCreating && (
            <form
              onSubmit={handleCreateAutomation}
              className="p-5 rounded-2xl bg-surface-50 dark:bg-surface-800/60 border border-brand-500/30 dark:border-brand-500/30 space-y-4"
            >
              <div className="flex items-center justify-between">
                <h4 className="font-bold text-xs text-brand-600 dark:text-brand-400 uppercase tracking-wider">
                  New Butler Automation Rule
                </h4>
                <button
                  type="button"
                  onClick={() => setIsCreating(false)}
                  className="text-xs text-surface-400 hover:text-surface-600"
                >
                  Cancel
                </button>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-surface-400 uppercase mb-1">
                  Rule Name
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Auto-complete card when all checklist items are checked"
                  className="w-full text-xs p-2.5 rounded-xl border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>

              {/* Trigger Logic */}
              <div className="p-3.5 bg-white dark:bg-surface-850 rounded-xl border border-surface-200 dark:border-surface-700 space-y-3">
                <span className="text-[11px] font-bold text-surface-500 block">
                  ⚡ WHEN (Trigger Event):
                </span>

                <div>
                  <select
                    value={triggerEvent}
                    onChange={(e) => setTriggerEvent(e.target.value as any)}
                    className="w-full p-2 rounded-lg bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs font-semibold"
                  >
                    <option value="status_changed">When a card moves to a status / list</option>
                    <option value="checklist_completed">
                      When all checklist items on a card are completed
                    </option>
                    <option value="task_created">When a new card is created on this board</option>
                  </select>
                </div>

                {triggerEvent === 'status_changed' && (
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-[10px] text-surface-400 block mb-1">
                        From Status (Optional):
                      </span>
                      <select
                        value={fromStatus}
                        onChange={(e) => setFromStatus(e.target.value)}
                        className="w-full p-2 rounded-lg bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs font-semibold"
                      >
                        <option value="">Any Status</option>
                        <option value="todo">To Do</option>
                        <option value="in_progress">In Progress</option>
                        <option value="in_review">In Review</option>
                      </select>
                    </div>
                    <div>
                      <span className="text-[10px] text-surface-400 block mb-1">
                        Moves To Status:
                      </span>
                      <select
                        value={toStatus}
                        onChange={(e) => setToStatus(e.target.value)}
                        className="w-full p-2 rounded-lg bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs font-semibold"
                      >
                        <option value="todo">To Do</option>
                        <option value="in_progress">In Progress</option>
                        <option value="in_review">In Review</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                  </div>
                )}

                <span className="text-[11px] font-bold text-surface-500 block pt-2 border-t border-surface-100 dark:border-surface-800">
                  🎯 THEN (Automated Action):
                </span>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-[10px] text-surface-400 block mb-1">Action Type:</span>
                    <select
                      value={actionType}
                      onChange={(e) => setActionType(e.target.value as any)}
                      className="w-full p-2 rounded-lg bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs font-semibold"
                    >
                      <option value="set_priority">Set Card Priority</option>
                      <option value="move_status">Move Card to Status</option>
                      <option value="assign_user">Assign Team Member</option>
                      <option value="add_label">Add Colored Label</option>
                      <option value="add_comment">Post Automated Comment</option>
                    </select>
                  </div>

                  {actionType === 'set_priority' && (
                    <div>
                      <span className="text-[10px] text-surface-400 block mb-1">
                        Target Priority:
                      </span>
                      <select
                        value={targetPriority}
                        onChange={(e) => setTargetPriority(e.target.value)}
                        className="w-full p-2 rounded-lg bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs font-semibold"
                      >
                        <option value="urgent">⚡ Urgent</option>
                        <option value="high">▲ High</option>
                        <option value="medium">● Medium</option>
                        <option value="low">▼ Low</option>
                      </select>
                    </div>
                  )}

                  {actionType === 'move_status' && (
                    <div>
                      <span className="text-[10px] text-surface-400 block mb-1">
                        Target Status:
                      </span>
                      <select
                        value={targetMoveStatus}
                        onChange={(e) => setTargetMoveStatus(e.target.value)}
                        className="w-full p-2 rounded-lg bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs font-semibold"
                      >
                        <option value="todo">To Do</option>
                        <option value="in_progress">In Progress</option>
                        <option value="in_review">In Review</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                  )}

                  {actionType === 'assign_user' && (
                    <div>
                      <span className="text-[10px] text-surface-400 block mb-1">
                        Assign Member:
                      </span>
                      <select
                        value={targetUserId}
                        onChange={(e) => setTargetUserId(e.target.value)}
                        className="w-full p-2 rounded-lg bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-xs font-semibold"
                      >
                        {users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.fullName}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {actionType === 'add_label' && (
                    <div>
                      <span className="text-[10px] text-surface-400 block mb-1">
                        Label Text & Color:
                      </span>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          value={targetLabelName}
                          onChange={(e) => setTargetLabelName(e.target.value)}
                          className="flex-1 p-1.5 rounded-lg border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 text-xs"
                        />
                        <select
                          value={targetLabelColor}
                          onChange={(e) => setTargetLabelColor(e.target.value)}
                          className="p-1.5 rounded-lg border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 text-xs"
                        >
                          {TRELLO_LABEL_PALETTE.map((l) => (
                            <option key={l.color} value={l.color}>
                              {l.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {actionType === 'add_comment' && (
                    <div>
                      <span className="text-[10px] text-surface-400 block mb-1">
                        Comment Text:
                      </span>
                      <input
                        type="text"
                        value={targetComment}
                        onChange={(e) => setTargetComment(e.target.value)}
                        className="w-full p-2 rounded-lg border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 text-xs"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsCreating(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl text-xs font-semibold bg-brand-600 text-white hover:bg-brand-700 shadow-sm"
                >
                  Save Automation Rule
                </button>
              </div>
            </form>
          )}

          {/* List of Existing Rules */}
          <div className="space-y-3">
            {automations.map((auto) => (
              <div
                key={auto.id}
                className="p-4 rounded-2xl bg-surface-50 dark:bg-surface-800/40 border border-surface-200 dark:border-surface-700/80 flex items-center justify-between"
              >
                <div className="flex items-start space-x-3">
                  <span className="p-2 rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-400 shrink-0">
                    <Zap className="w-4 h-4" />
                  </span>
                  <div>
                    <h5 className="font-semibold text-xs text-surface-900 dark:text-surface-100 mb-1">
                      {auto.title}
                    </h5>
                    <div className="flex flex-wrap items-center gap-2 text-[11px] text-surface-500">
                      <span>
                        {auto.triggerEvent === 'checklist_completed'
                          ? 'When Checklist 100% Complete'
                          : auto.triggerEvent === 'task_created'
                          ? 'When Card Created'
                          : `When Status → ${auto.triggerCondition?.toStatus || 'any'}`}
                      </span>
                      <ArrowRight className="w-3 h-3 text-surface-400" />
                      <span className="font-mono text-brand-600 dark:text-brand-400 font-bold">
                        {auto.actionType}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                    Active
                  </span>
                  <button
                    onClick={() => handleDelete(auto.id)}
                    className="p-1.5 text-surface-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}

            {automations.length === 0 && !isCreating && (
              <div className="p-8 text-center text-xs text-surface-400 bg-surface-50/50 dark:bg-surface-800/20 rounded-2xl border border-dashed border-surface-200 dark:border-surface-700">
                No active automation rules yet. Click "Create New Rule" to configure zero-code triggers.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
